import { z } from "zod";

import {
  COMMON_REQUIRED_ERROR,
  EMAIL_ERROR,
  MAX_NAME_LENGTH,
  MAX_PHONE_LENGTH,
  MESSAGE_MAX_LENGTH_ERROR,
  MESSAGE_MIN_LENGTH_ERROR,
  MIN_MESSAGE_LENGTH,
  MAX_MESSAGE_LENGTH,
  MIN_NAME_LENGTH,
  MIN_PHONE_LENGTH,
  NAME_MAX_LENGTH_ERROR,
  NAME_MIN_LENGTH_ERROR,
  PHONE_ERROR,
  PHONE_MAX_LENGTH_ERROR,
  PHONE_MIN_LENGTH_ERROR,
  PASSWORD_MAX_LENGTH_ERROR,
  PASSWORD_MIN_LENGTH_ERROR,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PASSWORD_TIP_NOT_WHITESPACE,
  WHITESPACE_REGEX,
  LOWERCASE_AND_UPPERCASE_REGEX,
  FIELD_PASSWORD_LOWERCASE_AND_UPPERCASE_TIP,
  SPECIAL_SYMBOLS_REGEX,
  FIELD_PASSWORD_SPECIAL_SYMBOLS_TIP,
  CONSENT_REQUIRED_ERROR,
  ZIP_CODE_ERROR,
} from "@/constants";
import { createTemplateString } from "./utils";

export const stringSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .min(1, COMMON_REQUIRED_ERROR);

export const nameSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .min(MIN_NAME_LENGTH, {
    message: createTemplateString(NAME_MIN_LENGTH_ERROR, [
      MIN_NAME_LENGTH.toString(),
    ]),
  })
  .max(MAX_NAME_LENGTH, {
    message: createTemplateString(NAME_MAX_LENGTH_ERROR, [
      MAX_NAME_LENGTH.toString(),
    ]),
  });

export const emailSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .trim()
  .email(EMAIL_ERROR);

export const passwordSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .trim()
  .min(MIN_PASSWORD_LENGTH, {
    message: createTemplateString(PASSWORD_MIN_LENGTH_ERROR, [
      MIN_PASSWORD_LENGTH.toString(),
    ]),
  })
  .max(MAX_PASSWORD_LENGTH, {
    message: createTemplateString(PASSWORD_MAX_LENGTH_ERROR, [
      MAX_PASSWORD_LENGTH.toString(),
    ]),
  })
  .regex(new RegExp(WHITESPACE_REGEX), PASSWORD_TIP_NOT_WHITESPACE);

export const passwordCreateSchema = passwordSchema
  .regex(
    new RegExp(LOWERCASE_AND_UPPERCASE_REGEX),
    FIELD_PASSWORD_LOWERCASE_AND_UPPERCASE_TIP
  )
  .regex(new RegExp(SPECIAL_SYMBOLS_REGEX), FIELD_PASSWORD_SPECIAL_SYMBOLS_TIP);

export const phoneSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .regex(/^\+?(?:[0-9] ?){6,14}[0-9]$/, PHONE_ERROR)
  .min(MIN_PHONE_LENGTH, {
    message: createTemplateString(PHONE_MIN_LENGTH_ERROR, [
      MIN_PHONE_LENGTH.toString(),
    ]),
  })
  .max(MAX_PHONE_LENGTH, {
    message: createTemplateString(PHONE_MAX_LENGTH_ERROR, [
      MAX_PHONE_LENGTH.toString(),
    ]),
  });

export const zipCodeSchema = z
  .string({ message: COMMON_REQUIRED_ERROR })
  .min(5, ZIP_CODE_ERROR);

export const fileSchema = z.object({
  handle: stringSchema,
  url: z.string().url("Invalid file URL"),
  filename: stringSchema,
  size: z.number().int().positive(),
  mimetype: stringSchema,
  status: stringSchema,
});

export const fileStackSchema = z.object({
  filename: z.string(),
  handle: z.string(),
  mimetype: z.string(),
  originalFile: z.union([z.instanceof(File), z.object({})]).optional(),
  originalPath: z.string(),
  size: z.number().int().positive(),
  source: z.string(),
  status: z.string(),
  uploadId: z.string(),
  url: z.string().url("Invalid file URL"),
  alt: z.string().optional(),
});

export const messageSchema = z
  .union([
    z
      .string()
      .min(MIN_MESSAGE_LENGTH, {
        message: createTemplateString(MESSAGE_MIN_LENGTH_ERROR, [
          MIN_MESSAGE_LENGTH.toString(),
        ]),
      })
      .max(MAX_MESSAGE_LENGTH, {
        message: createTemplateString(MESSAGE_MAX_LENGTH_ERROR, [
          MAX_MESSAGE_LENGTH.toString(),
        ]),
      }),
    z.string().length(0),
  ])
  .optional();

export const trueOnlySchema = z
  .union([z.literal(true), z.literal(false), z.undefined()])
  .refine((val) => val === undefined || val === true, {
    message: CONSENT_REQUIRED_ERROR,
  });

export const optionalStringSchema = z
  .union([
    z.string({ message: COMMON_REQUIRED_ERROR }).min(1, COMMON_REQUIRED_ERROR),
    z.string().length(0),
  ])
  .optional();

export const optionalPhoneSchema = z
  .union([
    z
      .string()
      .regex(/^\+?(?:[0-9] ?){6,14}[0-9]$/, PHONE_ERROR)
      .min(MIN_PHONE_LENGTH, {
        message: createTemplateString(PHONE_MIN_LENGTH_ERROR, [
          MIN_PHONE_LENGTH.toString(),
        ]),
      })
      .max(MAX_PHONE_LENGTH, {
        message: createTemplateString(PHONE_MAX_LENGTH_ERROR, [
          MAX_PHONE_LENGTH.toString(),
        ]),
      }),
    z.string().length(0),
  ])
  .optional();

export const optionalNameSchema = z
  .union([
    z
      .string()
      .min(MIN_NAME_LENGTH, {
        message: createTemplateString(NAME_MIN_LENGTH_ERROR, [
          MIN_NAME_LENGTH.toString(),
        ]),
      })
      .max(MAX_NAME_LENGTH, {
        message: createTemplateString(NAME_MAX_LENGTH_ERROR, [
          MAX_NAME_LENGTH.toString(),
        ]),
      }),
    z.string().length(0),
  ])
  .optional();

export const optionalEmailSchema = z
  .union([z.string().trim().email(EMAIL_ERROR), z.string().length(0)])
  .optional();

export const optionalZipCodeSchema = z
  .union([
    z.string({ message: COMMON_REQUIRED_ERROR }).min(5, ZIP_CODE_ERROR),
    z.string().length(0),
  ])
  .optional();

export const optionalPasswordSchema = z
  .union([
    z
      .string()
      .trim()
      .min(MIN_PASSWORD_LENGTH, {
        message: createTemplateString(PASSWORD_MIN_LENGTH_ERROR, [
          MIN_PASSWORD_LENGTH.toString(),
        ]),
      })
      .max(MAX_PASSWORD_LENGTH, {
        message: createTemplateString(PASSWORD_MAX_LENGTH_ERROR, [
          MAX_PASSWORD_LENGTH.toString(),
        ]),
      })
      .regex(new RegExp(WHITESPACE_REGEX), PASSWORD_TIP_NOT_WHITESPACE),
    z.string().length(0),
  ])
  .optional();

export const optionalPasswordCreateSchema = z
  .union([
    z
      .string()
      .trim()
      .min(MIN_PASSWORD_LENGTH, {
        message: createTemplateString(PASSWORD_MIN_LENGTH_ERROR, [
          MIN_PASSWORD_LENGTH.toString(),
        ]),
      })
      .max(MAX_PASSWORD_LENGTH, {
        message: createTemplateString(PASSWORD_MAX_LENGTH_ERROR, [
          MAX_PASSWORD_LENGTH.toString(),
        ]),
      })
      .regex(new RegExp(WHITESPACE_REGEX), PASSWORD_TIP_NOT_WHITESPACE)
      .regex(
        new RegExp(LOWERCASE_AND_UPPERCASE_REGEX),
        FIELD_PASSWORD_LOWERCASE_AND_UPPERCASE_TIP
      )
      .regex(
        new RegExp(SPECIAL_SYMBOLS_REGEX),
        FIELD_PASSWORD_SPECIAL_SYMBOLS_TIP
      ),
    z.string().length(0),
  ])
  .optional();

export const optionalTrueOnlySchema = z.boolean().optional();
